import { useParams } from "react-router-dom";
import { useAuthorData } from "./hooks/useAuthorData";
import AuthorProfileCard from "./components/AuthorProfileCard";
import AuthorPapersList from "./components/AuthorPapersList";
import LoadingState from "./components/LoadingState";
import NotFoundState from "./components/NotFoundState";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SEOHead from "@/components/common/SEOHead";
import { SITE_URL } from "@/lib/constants/urls";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Link } from "react-router-dom";

const AuthorProfilePage = () => {
  const { id } = useParams<{ id: string }>();
  const { author, papers, isLoading, isProfileHidden } = useAuthorData(id);

  const canonicalUrl = `${SITE_URL}/author/${id ?? ""}`;
  const seoTitle = author ? `${author.name} — Researcher on PaperVerse` : "Researcher — PaperVerse";
  const seoDescription = author
    ? (author.bio?.slice(0, 155) ||
        `Publications and research activity by ${author.name}${author.institution ? `, ${author.institution}` : ""} on PaperVerse.`)
    : "Researcher profile on PaperVerse.";

  const structuredData = author
    ? {
        "@context": "https://schema.org",
        "@type": "Person",
        name: author.name,
        ...(author.institution && {
          affiliation: { "@type": "Organization", name: author.institution },
        }),
        ...(author.bio && { description: author.bio }),
        url: canonicalUrl,
        sameAs: [author.website, author.twitter, author.linkedin, author.orcid].filter(Boolean),
      }
    : undefined;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonicalUrl={canonicalUrl}
        ogType="profile"
        structuredData={structuredData}
        noindex={isProfileHidden}
      />
      <Navbar />

      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4">
          <Breadcrumb className="mb-6">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/explore">Researchers</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{author?.name || "Researcher"}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {isLoading ? (
            <LoadingState />
          ) : isProfileHidden ? (
            <NotFoundState isPrivate={true} />
          ) : !author ? (
            <NotFoundState />
          ) : (
            <div className="space-y-8">
              <AuthorProfileCard author={author} paperCount={papers.length} papers={papers} />
              <AuthorPapersList papers={papers} authorName={author.name} />
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AuthorProfilePage;
